import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { API_URL } from '../config';
import { User } from '../types';

// Debug logging utility - no-op to avoid console noise
const debugLog = (_message: string, _data?: any) => {
  // Intentionally empty - use devtools for auth debugging
};

// Function to get debug logs - removed as we no longer store logs
export const getAuthDebugLogs = () => {
  return [];
};

// Function to clear debug logs - no longer needed
export const clearAuthDebugLogs = () => {
  // No longer storing logs in localStorage
};

interface AuthContextType {
  user: User | null;
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (loginId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (username: string, email: string, password: string, firstName: string, lastName: string, country: string) => Promise<void>;
  // Debug functions
  getDebugLogs: () => any[];
  clearDebugLogs: () => void;
  pauseRedirects: () => void;
  resumeRedirects: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [redirectsPaused, setRedirectsPaused] = useState(false);

  // Pause redirects for debugging
  const pauseRedirects = () => {
    setRedirectsPaused(true);
    debugLog('🛑 Redirects paused for debugging');
  };

  const resumeRedirects = () => {
    setRedirectsPaused(false);
    debugLog('▶️ Redirects resumed');
  };

  useEffect(() => {
    debugLog('🔄 Initializing auth state...');
    const userFromCookie = authService.getUserFromCookie();
    if (userFromCookie) {
      debugLog('✅ User found:', userFromCookie.username);
      setUser(userFromCookie);
      setIsAuthenticated(true);
    } else {
      debugLog('❌ No user found');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      debugLog('🔄 Periodic auth check...');
      const userFromCookie = authService.getUserFromCookie();
      if (userFromCookie) {
        debugLog('✅ User still authenticated:', userFromCookie.username);
        setUser(userFromCookie);
        setIsAuthenticated(true);
      } else {
        debugLog('❌ User no longer authenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };
    
    // Only run periodic checks if user is authenticated
    if (isAuthenticated) {
      checkAuth();
      const interval = setInterval(checkAuth, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Proactive token refresh to prevent logout due to inactivity
  useEffect(() => {
    const refreshToken = async () => {
      try {
        debugLog('🔄 Proactive token refresh...');
        const success = await authService.refreshToken();
        if (success) {
          debugLog('✅ Proactive token refresh successful');
        } else {
          debugLog('❌ Proactive token refresh failed');
        }
      } catch (error) {
        debugLog('❌ Error during proactive token refresh:', error);
      }
    };

    // Only refresh tokens if user is authenticated
    if (isAuthenticated) {
      // Refresh token every 20 minutes (before the 30-minute expiration)
      const refreshInterval = setInterval(refreshToken, 20 * 60 * 1000);
      
      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  const login = async (loginId: string, password: string) => {
    try {
      debugLog('🔐 Login attempt started for:', loginId);
      setIsLoading(true);
      const user = await authService.login(loginId, password);
      debugLog('✅ Login API call successful');
      
      // Set authentication state immediately from the response
      setUser(user);
      setIsAuthenticated(true);
      debugLog('✅ Login successful, authentication state set');
      
      // Store user data in localStorage as backup for immediate access
      localStorage.setItem('user', JSON.stringify(user));
      debugLog('✅ User data stored in localStorage as backup');
      
      // Verify user data is accessible
      const userFromCookie = authService.getUserFromCookie();
      if (userFromCookie) {
        debugLog('✅ User data confirmed accessible');
      } else {
        debugLog('⚠️ User data not accessible');
      }
    } catch (error) {
      debugLog('❌ Login error:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      debugLog('🚪 Logout attempt started');
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('user');
      debugLog('✅ Logout successful');
    } catch (error) {
      debugLog('❌ Logout error:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    }
  };

  const register = async (username: string, email: string, password: string, firstName: string, lastName: string, country: string) => {
    try {
      debugLog('📝 Register attempt started for:', username);
      const user = await authService.register(username, email, password, firstName, lastName, country);
      setUser(user);
      setIsAuthenticated(true);
      debugLog('✅ Register successful');
    } catch (error) {
      debugLog('❌ Register error:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('Register error:', error);
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      currentUser: user, 
      isAuthenticated, 
      isLoading, 
      login, 
      logout, 
      register,
      getDebugLogs: getAuthDebugLogs,
      clearDebugLogs: clearAuthDebugLogs,
      pauseRedirects,
      resumeRedirects
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 